import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Typography, withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';

import ViewScaffolding from '../common/ViewScaffolding';
import authApi from '../../api/authApi';
import { loginViewStyles } from './styles';

function getVerificationDescription(methods) {
    if (methods?.email && methods?.sms) {
        return 'A verification code has been sent to you by email and SMS.';
    }
    if (methods?.sms) {
        return 'A verification code has been sent to you by SMS.';
    }
    return 'A verification code has been sent to your email.';
}

function TwoFactorVerification({ classes, twoFactorToken, twoFactorMethods, onVerified, onBack }) {
    const [code, setCode] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const submit = useCallback(async () => {
        if (!code || code.length !== 6) {
            setErrorMessage('Please enter a 6-digit code');
            return;
        }
        try {
            const result = await authApi.verify2FA({ twoFactorToken, code });
            onVerified(result);
        } catch (error) {
            if (error && error.status === 403 && error.data?.message) {
                setErrorMessage(error.data.message);
            } else if (error && error.data && error.data.message) {
                setErrorMessage(error.data.message);
            } else if (error && error.status === 429) {
                setErrorMessage('You have had too many failed attempts. Please try again later.');
            } else {
                setErrorMessage('Verification failed. Please try again.');
            }
        }
    }, [code, twoFactorToken, onVerified]);

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        try {
            await authApi.resend2FA({ twoFactorToken });
            setResendCooldown(60);
            setErrorMessage(null);
        } catch (error) {
            if (error && error.data && error.data.message) {
                setErrorMessage(error.data.message);
            } else {
                setErrorMessage('Failed to resend code. Please try again.');
            }
        }
    };

    return (
        <ViewScaffolding
            description={getVerificationDescription(twoFactorMethods)}
            form={
                <form
                    onSubmit={event => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <TextField
                        id="2fa-code-input"
                        classes={{ root: classes.textField }}
                        InputLabelProps={{ className: classes.input }}
                        color="primary"
                        size="small"
                        label="Verification Code"
                        value={code}
                        onChange={event => {
                            const val = event.target.value.replace(/\D/g, '').slice(0, 6);
                            setCode(val);
                            setErrorMessage(null);
                        }}
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        autoFocus
                    />
                </form>
            }
            link={
                <>
                    <Typography
                        className={classes.link}
                        onClick={resendCooldown > 0 ? undefined : handleResend}
                        style={{
                            cursor: resendCooldown > 0 ? 'default' : 'pointer',
                            color: resendCooldown > 0 ? '#999' : undefined
                        }}
                    >
                        {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
                    </Typography>
                    <Typography className={classes.link} onClick={onBack} style={{ cursor: 'pointer', marginTop: 12 }}>
                        Back to login
                    </Typography>
                </>
            }
            action={submit}
            label="Verify"
            errorMessage={errorMessage}
        />
    );
}

TwoFactorVerification.propTypes = {
    classes: PropTypes.object.isRequired,
    twoFactorToken: PropTypes.string.isRequired,
    twoFactorMethods: PropTypes.object,
    onVerified: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired
};

export default withStyles(loginViewStyles)(TwoFactorVerification);
